import { useState } from 'react';
import { Button, Input, Segmented, Space, Table, Tag, message } from 'antd';
import {
  AppstoreOutlined,
  DownloadOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { OwnerType, SaleResult } from '@never2busy/shared';
import { salesApi, type Sale, type SaleListQuery } from '../../../api/endpoints/sales';
import { downloadBlob } from '../../../api/downloadBlob';
import { PageHeader } from '../../../components/common/PageHeader';
import { KeywordFilter } from '../../../components/common/KeywordFilter';
import { useSaleStageLabels } from '../useSaleStageLabels';

type ResultFilter = 'all' | SaleResult;

const RESULT_OPTIONS: { label: string; value: ResultFilter }[] = [
  { label: 'Todas', value: 'all' },
  { label: 'En negociación', value: SaleResult.PENDING },
  { label: 'Ganadas', value: SaleResult.WON },
  { label: 'Perdidas', value: SaleResult.LOST },
];

const RESULT_COLOR: Record<SaleResult, string> = {
  [SaleResult.PENDING]: 'processing',
  [SaleResult.WON]: 'success',
  [SaleResult.LOST]: 'error',
};

const RESULT_LABEL: Record<SaleResult, string> = {
  [SaleResult.PENDING]: 'Pendiente',
  [SaleResult.WON]: 'Ganada',
  [SaleResult.LOST]: 'Perdida',
};

export function SalesListPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ResultFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [keywordIds, setKeywordIds] = useState<number[]>([]);
  const stageLabels = useSaleStageLabels();

  const keywordIdsCsv = keywordIds.length > 0 ? keywordIds.join(',') : undefined;

  const queryParams: SaleListQuery = {
    page,
    pageSize,
    search: search || undefined,
    result: filter === 'all' ? undefined : filter,
    sortDir: 'desc',
    keywordIds: keywordIdsCsv,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['sales', queryParams],
    queryFn: () => salesApi.list(queryParams),
  });

  const handleExport = (): void => {
    downloadBlob('/sales/export.csv', 'ventas.csv', {
      search: search || undefined,
      result: filter === 'all' ? undefined : filter,
      keywordIds: keywordIdsCsv,
    }).catch(() => message.error('No se pudo exportar'));
  };

  const columns: ColumnsType<Sale> = [
    { title: 'Número', dataIndex: 'saleNumber', width: 120 },
    {
      title: 'Nombre',
      render: (_, row) => <Link to={`/ventas/${row.id}`}>{row.name}</Link>,
    },
    {
      title: 'Cuenta',
      render: (_, row) =>
        row.account ? <Link to={`/cuentas/${row.account.id}`}>{row.account.name}</Link> : '—',
    },
    {
      title: 'Etapa',
      dataIndex: 'stage',
      render: (v?: string | null) => (v ? <Tag>{stageLabels[v] ?? v}</Tag> : '—'),
    },
    {
      title: 'Resultado',
      dataIndex: 'result',
      render: (v: SaleResult) => <Tag color={RESULT_COLOR[v]}>{RESULT_LABEL[v]}</Tag>,
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      align: 'right',
      render: (v?: string | null, row?) =>
        v
          ? `${parseFloat(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })} ${row?.currency?.code ?? ''}`
          : '—',
    },
    {
      title: 'Asignada a',
      render: (_, row) =>
        row.assignedTo ? `${row.assignedTo.firstName} ${row.assignedTo.lastName}` : '—',
    },
    {
      title: 'Fecha',
      dataIndex: 'saleDate',
      render: (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY') : '—'),
    },
  ];

  return (
    <>
      <PageHeader
        title="Ventas"
        crumbs={[{ title: 'Inicio', to: '/' }, { title: 'Ventas' }]}
        extra={
          <Space wrap>
            <Button icon={<AppstoreOutlined />} onClick={() => navigate('/ventas/pipeline')}>
              Ver pipeline
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Exportar CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/ventas/nueva')}
            >
              Nueva venta
            </Button>
          </Space>
        }
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={RESULT_OPTIONS}
          value={filter}
          onChange={(v) => {
            setFilter(v as ResultFilter);
            setPage(1);
          }}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar por nombre, número o producto…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 320 }}
        />
        <KeywordFilter
          ownerType={OwnerType.SALE}
          value={keywordIds}
          onChange={(ids) => {
            setKeywordIds(ids);
            setPage(1);
          }}
        />
      </Space>

      <Table<Sale>
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={{
          current: page,
          pageSize,
          total: data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${total} ventas`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </>
  );
}
